import React from 'react';
import { CTASection } from '../../components/CTASection';
import { FAQSection } from '../../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, Activity, Stethoscope, TrendingUp, BookOpen, FileText } from 'lucide-react';
import { VideoSection, DR_REDDY_VIDEOS } from '../../components/YouTubeEmbed';
import { useSite } from '../../contexts/SiteContext';

export const ShoulderPain = () => {
  const { theme } = useSite();
  
  const faqs = [
    {
      question: 'When should I see a doctor for shoulder pain?',
      answer: 'Seek evaluation if: pain persists beyond 2-3 weeks, pain wakes you from sleep (a red flag for rotator cuff pathology), you cannot raise your arm overhead, there is significant weakness, or pain follows trauma. Immediate attention is needed for severe pain with inability to move, visible deformity, or signs of infection (fever, redness, warmth).'
    },
    {
      question: 'What are the most common causes of shoulder pain?',
      answer: 'Evidence from large population studies shows: Rotator cuff problems (tendinitis, tears) account for 65-70% of shoulder pain. Other causes include: frozen shoulder (adhesive capsulitis), shoulder impingement, glenohumeral arthritis, AC joint pathology, and biceps tendon problems. In patients over 40, rotator cuff pathology becomes increasingly common.'
    },
    {
      question: 'Can shoulder pain be treated without surgery?',
      answer: 'Yes - research consistently shows 70-80% of shoulder conditions improve with non-surgical treatment. This includes: structured physiotherapy (strongest evidence), activity modification, NSAIDs, and targeted injections when appropriate. Surgery is reserved for conditions that fail conservative care or specific pathologies requiring intervention.'
    },
    {
      question: 'How long does shoulder pain take to heal?',
      answer: 'Recovery timelines based on published data: Minor tendinitis/bursitis: 2-6 weeks. Impingement syndrome: 6-12 weeks. Frozen shoulder: 12-24 months (can be shortened with treatment). Rotator cuff tears (conservative): 3-6 months. Post-surgical recovery: 3-9 months depending on procedure.'
    },
    {
      question: 'What exercises can help with shoulder pain?',
      answer: 'Exercise therapy is the cornerstone of shoulder rehabilitation. Evidence supports: rotator cuff strengthening, scapular stabilization, posterior capsule stretching, and gradual progressive loading. However, exercises must be condition-specific - the wrong exercises can worsen certain conditions. Always get professional guidance for your specific diagnosis.'
    },
    {
      question: 'Why does my shoulder hurt at night?',
      answer: 'Night pain is characteristic of rotator cuff pathology and is considered a clinical indicator of significant shoulder disease. It occurs due to: lying on the affected side compressing inflamed structures, altered blood flow when lying flat, and loss of the distraction force from gravity. Persistent night pain warrants investigation.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Shoulder Pain Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-600">
              Expert diagnosis & care by Dr. Deepthi Nandan Reddy at Apollo Hospitals. Non-surgical options first.
            </p>
          </div>
        </div>
      </section>

      {/* What is Shoulder Pain */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What is Shoulder Pain?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Shoulder pain is one of the most common musculoskeletal complaints, affecting people of all ages. The shoulder is a complex ball-and-socket joint with a wide range of motion, making it susceptible to various injuries and conditions.
              </p>
              <p>
                The shoulder joint consists of three bones: the upper arm bone (humerus), shoulder blade (scapula), and collarbone (clavicle). These are held together by muscles, tendons, and ligaments. Pain can arise from any of these structures or from surrounding tissues.
              </p>
              <p>
                Shoulder pain may be localized to the joint itself or may radiate down the arm. It can range from mild discomfort to severe, debilitating pain that affects sleep and daily activities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Causes */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Common Causes of Shoulder Pain
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Rotator Cuff Problems
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Tears, tendonitis, or inflammation of the rotator cuff muscles and tendons that stabilize the shoulder</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Frozen Shoulder
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Adhesive capsulitis causing stiffness and progressive loss of shoulder motion</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Arthritis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Osteoarthritis or rheumatoid arthritis affecting the shoulder joint</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Shoulder Impingement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Compression of rotator cuff tendons causing pain with overhead activities</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Bursitis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Inflammation of the bursa, fluid-filled sacs that cushion the shoulder joint</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-medical-orange" />
                    Injury or Trauma
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Fractures, dislocations, or soft tissue injuries from falls or accidents</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Symptoms
              </h2>
            </div>
            <div className="bg-orange-50 p-6 rounded-lg space-y-3">
              <p className="text-gray-700">• Pain that worsens with movement or at night</p>
              <p className="text-gray-700">• Limited range of motion in the shoulder</p>
              <p className="text-gray-700">• Weakness in the arm or shoulder</p>
              <p className="text-gray-700">• Stiffness and difficulty reaching overhead</p>
              <p className="text-gray-700">• Popping or clicking sounds with movement</p>
              <p className="text-gray-700">• Swelling or tenderness around the joint</p>
              <p className="text-gray-700">• Pain radiating down the arm</p>
            </div>
          </div>
        </div>
      </section>

      {/* Diagnosis */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Stethoscope className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Diagnosis
              </h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                A comprehensive evaluation includes:
              </p>
              <p>
                <strong>Medical History:</strong> Discussion of symptoms, previous injuries, and activities that trigger pain
              </p>
              <p>
                <strong>Physical Examination:</strong> Assessment of range of motion, strength, stability, and pain patterns
              </p>
              <p>
                <strong>Imaging Studies:</strong> X-rays to check for bone abnormalities, MRI scans to evaluate soft tissues like tendons and ligaments, or ultrasound for real-time assessment of shoulder structures
              </p>
              <p>
                Accurate diagnosis is essential for developing an effective treatment plan tailored to your specific condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-medical-orange" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Treatment Options
              </h2>
            </div>
            <div className="space-y-6">
              <Card className="border-l-4 border-l-medical-orange">
                <CardHeader>
                  <CardTitle>Conservative Treatment</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700 space-y-2">
                  <p>• Rest and activity modification</p>
                  <p>• Ice therapy to reduce inflammation</p>
                  <p>• Anti-inflammatory medications</p>
                  <p>• Physiotherapy and strengthening exercises</p>
                  <p>• Corticosteroid injections for persistent pain</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-medical-orange">
                <CardHeader>
                  <CardTitle>When Surgery is Needed</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-700">
                  <p className="mb-3">
                    Surgical intervention may be recommended when:
                  </p>
                  <p>• Conservative treatments have not provided relief after 3-6 months</p>
                  <p>• There is a complete rotator cuff tear</p>
                  <p>• Severe arthritis causing significant disability</p>
                  <p>• Recurrent dislocations or instability</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Recovery */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Recovery Expectations
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Recovery from shoulder pain depends on the underlying cause and treatment approach:
              </p>
              <p>
                <strong>Conservative Treatment:</strong> Many patients experience significant improvement within 6-12 weeks with proper physiotherapy and adherence to treatment recommendations.
              </p>
              <p>
                <strong>Post-Surgical Recovery:</strong> Recovery varies by procedure but typically involves 6-12 months for full return to activities. Early physiotherapy is crucial for optimal outcomes.
              </p>
              <p>
                Commitment to rehabilitation exercises and follow-up care is essential for achieving the best possible results and preventing recurrence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <VideoSection video={DR_REDDY_VIDEOS.commonMistakes} theme={theme} />

      {/* FAQs */}
      <FAQSection faqs={faqs} />

      {/* CTA */}
      <CTASection />
    </div>
  );
};